import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { alpha } from '@mui/material/styles';
import apiService from '../services/ApiService';
import authService from '../services/AuthService';
import LoadingSpinner from '../components/LoadingSpinner';
import RankingComparisonChart from '../components/RankingComparisonChart';
import { DayBadge, Last5DaysLegend } from '../components/Last5DaysBadges';
import { debugError } from '../utils/debugUtils';
import { getPeriodKey, shiftDate, formatPeriodLabel } from '../utils/periodNavigation';

const TABS = [
  { labelKey: 'leaderboard.tabGlobal', type: null },
  { labelKey: 'leaderboard.tabWeekly', type: 'WEEKLY' },
  { labelKey: 'leaderboard.tabMonthly', type: 'MONTHLY' },
  { labelKey: 'leaderboard.tabYearly', type: 'YEARLY' },
  { labelKey: 'leaderboard.tabComparison', type: 'COMPARISON' },
];

function MedalIcon({ rank }) {
  if (rank === 1) return <span style={{ fontSize: '1.1rem' }}>🥇</span>;
  if (rank === 2) return <span style={{ fontSize: '1.1rem' }}>🥈</span>;
  if (rank === 3) return <span style={{ fontSize: '1.1rem' }}>🥉</span>;
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
      {rank}
    </Typography>
  );
}

const highlightSx = {
  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
  borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
  '& td': { fontWeight: 700 },
};

function PlayerRow({ p, isCurrentUser, showLast5, navigate, t }) {
  return (
    <TableRow
      sx={{
        bgcolor: isCurrentUser ? undefined : p.rank <= 3 ? 'action.hover' : 'inherit',
        '&:last-child td': { border: 0 },
        ...(isCurrentUser ? highlightSx : {}),
      }}
    >
      <TableCell>
        <MedalIcon rank={p.rank} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={isCurrentUser || p.rank <= 3 ? 700 : 400} component="span">
          {p.name}
        </Typography>
        {isCurrentUser && (
          <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 0.5 }}>
            ({t('results.yourWordBadge')})
          </Typography>
        )}
      </TableCell>
      {showLast5 && (
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            {(p.last5Days || []).map((day, idx) => (
              <DayBadge
                key={idx}
                score={day.score}
                date={day.date}
                onClick={day.date ? () => navigate(`/history?date=${day.date}`) : undefined}
              />
            ))}
          </Box>
        </TableCell>
      )}
      <TableCell align="right">
        <Typography variant="body2" fontWeight={700} color={isCurrentUser ? 'text.secondary' : 'primary.main'}>
          {p.totalScore}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

export default function LeaderboardPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [offset, setOffset] = useState(0);
  const [topEntries, setTopEntries] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const user = authService.getUser();
  const showLast5 = activeTab === 0;
  const tab = TABS[activeTab];

  const handleTabChange = (_, v) => {
    setActiveTab(v);
    setOffset(0);
  };

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const referenceDate = tab.type && tab.type !== 'COMPARISON' ? shiftDate(yesterday, tab.type, offset) : null;
  const periodLabel = referenceDate ? formatPeriodLabel(tab.type, referenceDate, i18n.language) : null;

  useEffect(() => {
    setLoading(true);
    setError(null);
    setCurrentUser(null);

    if (!tab.type || tab.type === 'COMPARISON') {
      // Ranking global: devuelve array plano (sin RankingResponse)
      apiService
        .get('/api/v1/game/scores')
        .then((data) => {
          const all = Array.isArray(data) ? data : [];
          const userName = user ? (user.name?.trim() ? user.name : user.email) : null;
          const top = all.slice(0, 10);
          const userEntry = userName ? all.find((p) => p.name === userName) : null;
          const inTop = userEntry && userEntry.rank <= 10;
          setTopEntries(top);
          setCurrentUser(inTop ? null : userEntry || null);
        })
        .catch((err) => {
          debugError('[LeaderboardPage] Error loading global scores:', err);
          setError(err.message);
        })
        .finally(() => setLoading(false));
    } else {
      const periodKey = getPeriodKey(tab.type, referenceDate);
      apiService
        .get(`/api/v1/game/rankings?type=${tab.type}&periodKey=${periodKey}`)
        .then((data) => {
          setTopEntries(Array.isArray(data?.topEntries) ? data.topEntries : []);
          setCurrentUser(data?.currentUser ?? null);
        })
        .catch((err) => {
          debugError('[LeaderboardPage] Error loading rankings:', err);
          setError(err.message);
        })
        .finally(() => setLoading(false));
    }
  }, [activeTab, offset]);

  const userName = user ? (user.name?.trim() ? user.name : user.email) : null;

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 700, mx: 'auto', width: '100%' }}>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        color="white"
        sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
      >
        {t('leaderboard.title')}
      </Typography>

      <Card sx={{ width: '100%' }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            {TABS.map((tabItem, i) => (
              <Tab key={i} label={t(tabItem.labelKey)} />
            ))}
          </Tabs>

          {tab.type && tab.type !== 'COMPARISON' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <IconButton
                size="small"
                aria-label={t('leaderboard.previousPeriod')}
                onClick={() => setOffset((o) => o + 1)}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 160, textAlign: 'center' }}>
                {periodLabel}
              </Typography>
              <IconButton
                size="small"
                aria-label={t('leaderboard.nextPeriod')}
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - 1))}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}

          {tab.type === 'COMPARISON' ? (
            loading ? (
              <Box sx={{ py: 4 }}>
                <LoadingSpinner />
              </Box>
            ) : (
              <RankingComparisonChart groupId={null} initialTopEntries={topEntries} t={t} />
            )
          ) : loading ? (
            <Box sx={{ py: 4 }}>
              <LoadingSpinner />
            </Box>
          ) : error ? (
            <Typography textAlign="center" color="error" py={4}>
              {t('common.error')}
            </Typography>
          ) : topEntries.length === 0 ? (
            <Typography textAlign="center" color="text.secondary" py={4}>
              {t('leaderboard.noScores')}
            </Typography>
          ) : (
            <>
              {showLast5 && <Last5DaysLegend t={t} />}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 48 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('leaderboard.player')}</TableCell>
                      {showLast5 && (
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          {t('leaderboard.last5Days')}
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {t('leaderboard.total')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topEntries.map((p) => (
                      <PlayerRow
                        key={p.rank}
                        p={p}
                        isCurrentUser={!!userName && p.name === userName}
                        showLast5={showLast5}
                        navigate={navigate}
                        t={t}
                      />
                    ))}

                    {currentUser && (
                      <>
                        <TableRow>
                          <TableCell colSpan={showLast5 ? 4 : 3} align="center" sx={{ py: 0.5, border: 0 }}>
                            <Typography variant="caption" color="text.disabled">···</Typography>
                          </TableCell>
                        </TableRow>
                        <PlayerRow
                          key="current-user"
                          p={currentUser}
                          isCurrentUser={true}
                          showLast5={showLast5}
                          navigate={navigate}
                          t={t}
                        />
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
